import { parseFullName } from "parse-full-name";
import { convert } from "html-to-text";
import dayjs from "dayjs";


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function POJO(data: any, depth: "full" | "first" | "none" = "first"): any {
  if (depth === "none") return data;  // Return data as-is (no conversion)

  // First, handle null and ensure data is an object or array
  if (data === null || typeof data !== 'object') {
    return data; // Return non-object data as-is (primitives or null)
  }

  // Check if data is an array and process each element
  if (Array.isArray(data)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((item: any) => POJO(item, depth));  // Recursively process each array item
  }

  // Process object data
  const newData = {...data};
  const keys = Object.keys(newData);
  for (const key of keys) {
      if (newData[key] && ((newData[key]._seconds >= 0 && newData[key]._nanoseconds >= 0) || (newData[key].seconds >= 0 && newData[key].nanoseconds >= 0))) {
          // Convert Timestamp to JavaScript Date
          try {
            newData[key] = newData[key].toDate();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            if (newData[key].seconds) {
              newData[key] = dayjs.unix(newData[key].seconds).toDate();
            } else if (newData[key]._seconds) {
              newData[key] = dayjs.unix(newData[key]._seconds).toDate();
            }
          }
      } else if (depth === "full" && typeof newData[key] === 'object' && newData[key] !== null) {
          // Recursive check for nested objects and arrays
          newData[key] = POJO(newData[key], depth);
      }
  }
  return newData;
}

export function urlFix(str: string) {
  return str.toLowerCase().replace(/and /gi, "").replace(/ /gi, "-").replace(/\./gi, "").replace(/[^a-z0-9\-. ]/gi, '');
}

export function urlEncodeAuthor(author: string) {
  return author.toLowerCase().replace(' ', '-')
}

export function convertCase(str: string): string {
  const lower = String(str).toLowerCase().replace(/-/gi, " ");
  const upper = lower
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return upper;
}

export function formatCurrency(amount: number, currency: string, precision: number = 2) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: precision }).format(amount);
}

export function html2text(content: string) {
    const options = {
      selectors: [
        {selector: "a", format: "skip"},
        {selector: "img", format: "skip"},
        {selector: "h2", options: {uppercase: false}},
        {selector: "h3", options: {uppercase: false}},
        {selector: "h4", options: {uppercase: false}},
      ],
      tables: true,
      preserveNewlines: true
    };
    return convert(content, options);
}

export function wait(delayInMilliseconds: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, delayInMilliseconds);
  });
}

export function fetchRetry(url: string, options: RequestInit | undefined, params: {retries: number, retryDelay: number}): Promise<Response> {
  return fetch(url, options)
    .catch(async (error) => {
      if (params.retries === 1) throw error;
      await wait (params.retryDelay);
      return fetchRetry(url, options, {retries: params.retries - 1, retryDelay: params.retryDelay});
  });
}

export function isItBot() {
	const bots = [
		"adsbot-google", "adsbot-google-mobile", "adidxbot", "amznkassocbot",
		"mediapartners-google", "storebot", "inspectiontool", "googleother",
		"google-extended", "feedfetcher", "googleproducer", "google web preview",
		"google-adwords-displayads", "google-adwords-displayads-webrender",
		"google-structured-data-testing-tool", "googlebot", "chrome-lighthouse",
		"bingbot", "yandexbot", "duckduckbot", "slurp", "yahoo", "baiduspider",
		"facebookexternalhit", "twitterbot", "rogerbot", "linkedinbot", "embedly",
		"quora link preview", "showyoubot", "outbrain", "pinterest", "slackbot",
		"vkshare", "w3c_validator", "redditbot", "applebot", "whatsapp", "flipboard",
		"tumblr", "bitlybot", "skypeuripreview", "nuzzel", "discordbot", "qwantify",
		"pinterestbot", "bitrix link preview", "xing-contenttabreceiver", "semrushbot",
		"semrushbot-sa", "semrush", "rogerbot", "ahrefsbot", "ahrefssiteaudit", "ahrefs",
		"screaming", "perplexitybot"
	];

  if (!window?.navigator?.userAgent) return "";
  const userAgent = window.navigator.userAgent.toLowerCase();
  return bots.find(bot => userAgent.includes(bot)) || "";
}

export function splitTextByRegex(text: string, regex: RegExp) {
  // Find all matches for regex tags along with their indices
  const matches = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
      matches.push(match.index);
  }

  // Determine the splitting points based on the number of regex tags
  let parts;
  if (matches.length >= 6) {
		// If there are three or more regex tags, split before the second and after the third regex tags
		parts = [
				text.substring(0, matches[1]),
				text.substring(matches[1], matches[3]),
				text.substring(matches[3], matches[5]),
				text.substring(matches[5])
		];
	}	else if (matches.length == 4 || matches.length == 5) {
      // If there are three or more regex tags, split before the second and after the third regex tags
      parts = [
          text.substring(0, matches[1]),
          text.substring(matches[1], matches[3]),
          text.substring(matches[3])
      ];
  } else if (matches.length == 3) {
		// If there are two regex tags, split after the second regex tag
		parts = [
			text.substring(0, matches[1]),
			text.substring(matches[1], matches[2]),
			text.substring(matches[2])
		];
	}	else if (matches.length == 2) {
      // If there are two regex tags, split after the second regex tag
      parts = [
          text.substring(0, matches[1]),
          text.substring(matches[1])
      ];
  } else if (matches.length == 1) {
      // If there is only one regex tag, split before it and return the rest of the text as the second part
      parts = [
          text.substring(0, matches[0]),
          text.substring(matches[0])
      ];
  } else {
      // If there are no regex tags, return the entire text as a single part
      parts = [text];
  }

  return parts;
}

export function splitTextIntoChunks(text:string) {
	let minChunkSize = 1500;
	const chunks = [];
	let startIndex = 0;
	
	while (startIndex < text.length) {
		// Start by setting the endIndex to minChunkSize away from startIndex
		let endIndex = startIndex + minChunkSize;

		// Extend endIndex if it is less than the text length
		if (endIndex < text.length) {
			const pTagIndex = text.indexOf('</p>', endIndex);
	
			// If a </p> tag is found after endIndex, update endIndex to the index after </p>
			if (pTagIndex !== -1) {
				endIndex = pTagIndex + 4;
			} else {
				// If no </p> tag is found after the endIndex, set endIndex to the text length
				endIndex = text.length;
			}

			// Extract tentative chunk for further examination
			let chunk = text.substring(startIndex, endIndex);
			
			// Check if the chunk breaks a div with id "donotbreak"
			const startDivIndex = chunk.indexOf('<div id="donotbreak">');
			const endDivIndex = chunk.indexOf('</div>', startDivIndex);
			
			if (startDivIndex !== -1 && (endDivIndex === -1 || endDivIndex >= chunk.length)) {
				// Find the complete </div> tag position for the "donotbreak" div
				const completeEndDivIndex = text.indexOf('</div>', startIndex + startDivIndex);
				if (completeEndDivIndex !== -1) {
					endIndex = completeEndDivIndex + 6;  // Include the complete </div> tag
					chunk = text.substring(startIndex, endIndex);  // Update the chunk
				}
			}

			// Add chunk to the chunks array
			chunks.push(chunk);

			// Move startIndex to the end of the extracted chunk
			startIndex = endIndex;
		} else {
			// If endIndex exceeds text length, set it to the text length
			endIndex = text.length;
			
			// Extract the remaining chunk and add it to the chunks array
			const chunk = text.substring(startIndex, endIndex);
			chunks.push(chunk);
			
			// Move startIndex to the end of the extracted chunk
			startIndex = endIndex;
		}
		
		// Cut further down the text if the chunk is too small
		minChunkSize = minChunkSize + 3000;
	}

	return chunks;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function processCaptureContact(data: any) {
  const name = parseFullName(data.displayName);
  return {...data, firstname: name.first, lastname: name.last, email: data.email.trim().toLowerCase(), authEmail: data.email.trim().toLowerCase()};
}